import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { notificationService } from '../services';
import { authService } from '../services';
import { io } from 'socket.io-client';

/**
 * Notification Context
 * Manages in-app notifications with backend persistence and WebSocket real-time updates
 */

// Determine socket URL at runtime based on current environment
const getSocketUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL.replace('/api', '');
  }
  // Runtime check: if we're not on localhost, we're in production
  const isLocalhost = window.location.hostname === 'localhost' ||
                      window.location.hostname === '127.0.0.1';
  if (!isLocalhost) {
    return window.location.origin;
  }
  return 'http://localhost:5000';
};

const SOCKET_URL = getSocketUrl();

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [toasts, setToasts] = useState([]);
  const [loading, setLoading] = useState(false);
  const socketRef = useRef(null);

  // Fetch notifications from API
  const fetchNotifications = useCallback(async () => {
    if (!authService.isAuthenticated()) return;

    // Always skip on participant session pages — notifications require full auth
    // (protect middleware) and a 401 here could disrupt the live session.
    // Check at call time since this callback is reused across navigations.
    const currentPath = window.location.pathname;
    if (currentPath.startsWith('/session/') || currentPath.startsWith('/join')) {
      return;
    }

    try {
      setLoading(true);
      const response = await notificationService.getNotifications(1, 50);
      if (response.success) {
        setNotifications(response.data || []);
        setUnreadCount(response.unreadCount || 0);
      }
    } catch (error) {
      // Silently ignore on session pages (user may have navigated here after fetch started)
      if (!window.location.pathname.startsWith('/session/')) {
        console.error('Error fetching notifications:', error);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Initialize socket connection for real-time notifications
  // Only for authenticated users on dashboard/admin pages, NOT on participant session pages.
  useEffect(() => {
    if (!authService.isAuthenticated()) return;

    const currentPath = window.location.pathname;
    if (currentPath.startsWith('/session/') || currentPath.startsWith('/join')) {
      return;
    }

    const token = authService.getToken();

    socketRef.current = io(SOCKET_URL, {
      auth: { token },
      transports: ['websocket', 'polling'],
    });

    socketRef.current.on('notification:new', (notification) => {
      // Don't process notifications if user navigated to session page
      if (window.location.pathname.startsWith('/session/')) return;

      setNotifications((prev) => [notification, ...prev]);
      setUnreadCount((prev) => prev + 1);

      if (notification.priority === 'high' || notification.priority === 'urgent') {
        showToast({
          id: notification._id || notification.id,
          type: notification.type,
          title: notification.title,
          message: notification.message,
          icon: getIconForType(notification.type),
          color: notification.color || 'blue',
          actionUrl: notification.actionUrl,
          actionText: notification.actionText,
        });
      }
    });

    socketRef.current.on('connect', () => {
      console.log('Notification socket connected');
    });

    socketRef.current.on('disconnect', () => {
      console.log('Notification socket disconnected');
    });

    // Cleanup on unmount
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, []);

  // Fetch notifications on mount
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Get icon based on notification type
  const getIconForType = (type) => {
    const icons = {
      session_assigned: '📅',
      session_created: '✨',
      session_started: '▶️',
      session_completed: '✅',
      session_reminder: '⏰',
      trainer_invited: '📨',
      report_ready: '📊',
      activity_completed: '🎯',
      new_participant: '👋',
      system: '🔔',
      activity_launched: '📝',
      qa_answered: '💬',
    };
    return icons[type] || '🔔';
  };

  /**
   * Add a new notification (for local/activity notifications)
   */
  const addNotification = useCallback((notification) => {
    const newNotification = {
      id: Date.now() + Math.random(),
      _id: Date.now().toString(),
      isRead: false,
      createdAt: new Date().toISOString(),
      ...notification,
    };

    setNotifications((prev) => [newNotification, ...prev]);
    setUnreadCount((prev) => prev + 1);

    // Also show as toast if priority is high or urgent
    if (notification.priority === 'high' || notification.priority === 'urgent') {
      showToast({
        ...notification,
        id: newNotification.id,
      });
    }

    return newNotification;
  }, []);

  /**
   * Mark notification as read
   */
  const markAsRead = useCallback(async (notificationId) => {
    // Optimistically update UI
    setNotifications((prev) =>
      prev.map((n) => {
        const id = n._id || n.id;
        return id === notificationId ? { ...n, isRead: true, readAt: new Date().toISOString() } : n;
      })
    );
    setUnreadCount((prev) => Math.max(0, prev - 1));

    // Sync with backend
    try {
      await notificationService.markAsRead(notificationId);
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }, []);

  /**
   * Mark all as read
   */
  const markAllAsRead = useCallback(async () => {
    // Optimistically update UI
    setNotifications((prev) =>
      prev.map((n) => ({
        ...n,
        isRead: true,
        readAt: new Date().toISOString(),
      }))
    );
    setUnreadCount(0);

    // Sync with backend
    try {
      await notificationService.markAllAsRead();
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  }, []);

  /**
   * Delete notification
   */
  const deleteNotification = useCallback(async (notificationId) => {
    // Optimistically update UI
    setNotifications((prev) => {
      const notification = prev.find((n) => (n._id || n.id) === notificationId);
      if (notification && !notification.isRead) {
        setUnreadCount((count) => Math.max(0, count - 1));
      }
      return prev.filter((n) => (n._id || n.id) !== notificationId);
    });

    // Sync with backend
    try {
      await notificationService.deleteNotification(notificationId);
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  }, []);

  /**
   * Clear all notifications
   */
  const clearAll = useCallback(async () => {
    setNotifications([]);
    setUnreadCount(0);

    // Sync with backend
    try {
      await notificationService.deleteAllNotifications();
    } catch (error) {
      console.error('Error clearing notifications:', error);
    }
  }, []);

  /**
   * Show toast notification (temporary pop-up)
   */
  const showToast = useCallback((toast) => {
    const newToast = {
      id: Date.now() + Math.random(),
      duration: 5000, // 5 seconds default
      ...toast,
    };

    setToasts((prev) => [...prev, newToast]);

    // Auto-remove after duration
    setTimeout(() => {
      removeToast(newToast.id);
    }, newToast.duration);

    return newToast.id;
  }, []);

  /**
   * Remove toast
   */
  const removeToast = useCallback((toastId) => {
    setToasts((prev) => prev.filter((t) => t.id !== toastId));
  }, []);

  /**
   * Helper: Create activity launch notification
   */
  const notifyActivityLaunch = useCallback(
    (activity) => {
      return addNotification({
        type: 'activity_launched',
        title: 'New Activity Started!',
        message: activity.title || 'Your trainer has started a new activity',
        icon: activity.icon || '📝',
        color: 'blue',
        priority: 'high',
        actionUrl: `/session/${activity.sessionId}/active`,
        actionText: 'Join Activity',
        metadata: {
          activityId: activity.id,
          activityType: activity.type,
          sessionId: activity.sessionId,
        },
      });
    },
    [addNotification]
  );

  /**
   * Helper: Create session reminder notification
   */
  const notifySessionReminder = useCallback(
    (session, minutesUntilStart) => {
      return addNotification({
        type: 'session_reminder',
        title: 'Session Starting Soon!',
        message: `${session.name} starts in ${minutesUntilStart} minutes`,
        icon: '⏰',
        color: 'orange',
        priority: 'high',
        actionUrl: `/join?code=${session.code}`,
        actionText: 'Join Now',
        metadata: {
          sessionId: session.id,
          sessionCode: session.code,
        },
      });
    },
    [addNotification]
  );

  /**
   * Helper: Create report ready notification
   */
  const notifyReportReady = useCallback(
    (session) => {
      return addNotification({
        type: 'report_ready',
        title: 'Your Session Report is Ready',
        message: `Download your performance report for "${session.name}"`,
        icon: '📊',
        color: 'green',
        priority: 'normal',
        actionUrl: `/sessions/${session.id}/report`,
        actionText: 'View Report',
        metadata: {
          sessionId: session.id,
        },
      });
    },
    [addNotification]
  );

  /**
   * Helper: Create Q&A answered notification
   */
  const notifyQAAnswered = useCallback(
    (question, sessionId) => {
      return addNotification({
        type: 'qa_answered',
        title: 'Your Question Was Answered',
        message: `The trainer responded to: "${question.substring(0, 50)}..."`,
        icon: '💬',
        color: 'blue',
        priority: 'normal',
        actionUrl: `/session/${sessionId}/active`,
        actionText: 'View Answer',
        metadata: {
          questionId: question.id,
          sessionId: sessionId,
        },
      });
    },
    [addNotification]
  );

  /**
   * Refresh notifications from server
   */
  const refreshNotifications = useCallback(() => {
    return fetchNotifications();
  }, [fetchNotifications]);

  const value = {
    // Notifications state
    notifications,
    unreadCount,
    toasts,
    loading,

    // Notification actions
    addNotification,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAll,
    refreshNotifications,

    // Toast actions
    showToast,
    removeToast,

    // Helper methods
    notifyActivityLaunch,
    notifySessionReminder,
    notifyReportReady,
    notifyQAAnswered,
  };

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
};

export default NotificationContext;
